/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ClassCreate } from "../models/ClassCreate";
import type { ClassDetail } from "../models/ClassDetail";
import type { ClassEnroll } from "../models/ClassEnroll";
import type { ClassEnrollResponse } from "../models/ClassEnrollResponse";
import type { ClassRow } from "../models/ClassRow";
import type { ClassUpdate } from "../models/ClassUpdate";
import type { PublicClass } from "../models/PublicClass";
import type { PublicEnrollCreate } from "../models/PublicEnrollCreate";
import type { PublicEnrollResponse } from "../models/PublicEnrollResponse";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class ClassesService {
  /**
   * Get Public Classes
   * The org's classes open for enrollment, for its public Classes tab.
   * @returns PublicClass Successful Response
   * @throws ApiError
   */
  public static getPublicClasses({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<PublicClass>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/classes/public/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Classes
   * The Classes page: current classes, or past ones.
   * @returns ClassRow Successful Response
   * @throws ApiError
   */
  public static getClasses({
    orgId,
    past = false,
  }: {
    orgId: number;
    past?: boolean;
  }): CancelablePromise<Array<ClassRow>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/classes",
      query: {
        org_id: orgId,
        past: past,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Class
   * A new class and its weekly times, in one transaction.
   * @returns ClassDetail Successful Response
   * @throws ApiError
   */
  public static createClass({
    requestBody,
  }: {
    requestBody: ClassCreate;
  }): CancelablePromise<ClassDetail> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/classes",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Class Detail
   * A class in its editor, with its roster.
   * @returns ClassDetail Successful Response
   * @throws ApiError
   */
  public static getClassDetail({
    classId,
  }: {
    classId: number;
  }): CancelablePromise<ClassDetail> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/classes/{class_id}",
      path: {
        class_id: classId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Class
   * Save the class editor: the class's facts, and each weekly time from its
   * next unlocked session on (past sessions keep their history). New weekly
   * times are added with the current roster; ones left out are cancelled
   * from their next session. A class keeps at least one weekly time.
   * @returns ClassDetail Successful Response
   * @throws ApiError
   */
  public static updateClass({
    classId,
    requestBody,
  }: {
    classId: number;
    requestBody: ClassUpdate;
  }): CancelablePromise<ClassDetail> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/classes/{class_id}",
      path: {
        class_id: classId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Class
   * Cancel the class's sessions still to come and delete the class. Past
   * sessions stay as ordinary meetings in history.
   * @returns void
   * @throws ApiError
   */
  public static deleteClass({
    classId,
  }: {
    classId: number;
  }): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/classes/{class_id}",
      path: {
        class_id: classId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Enroll Class Student
   * Add a student to every remaining session (admins may go over the seats).
   * @returns ClassEnrollResponse Successful Response
   * @throws ApiError
   */
  public static enrollClassStudent({
    classId,
    requestBody,
  }: {
    classId: number;
    requestBody: ClassEnroll;
  }): CancelablePromise<ClassEnrollResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/classes/{class_id}/students",
      path: {
        class_id: classId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Unenroll Class Student
   * Take a student off the remaining sessions they haven't been billed for.
   * @returns ClassEnrollResponse Successful Response
   * @throws ApiError
   */
  public static unenrollClassStudent({
    classId,
    studentId,
    meetingIds,
  }: {
    classId: number;
    studentId: number;
    meetingIds?: Array<number> | null;
  }): CancelablePromise<ClassEnrollResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/classes/{class_id}/students/{student_id}",
      path: {
        class_id: classId,
        student_id: studentId,
      },
      query: {
        meeting_ids: meetingIds,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Enroll In Class Publicly
   * A family enrolls from the public Classes tab: their students and or
   * someone new, with the phone-code token. Instant; seats are counted under
   * a lock on the class, and the org's admins are told.
   * @returns PublicEnrollResponse Successful Response
   * @throws ApiError
   */
  public static enrollInClassPublicly({
    classId,
    requestBody,
  }: {
    classId: number;
    requestBody: PublicEnrollCreate;
  }): CancelablePromise<PublicEnrollResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/classes/{class_id}/public_enroll",
      path: {
        class_id: classId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
